import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Users,
  CheckCircle,
  AlertTriangle,
  Receipt,
  Wallet,
  ArrowUpRight,
  Activity,
  Router,
  Radio,
  RefreshCw,
  Plus,
  TrendingUp,
  X,
  ChevronRight
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from "recharts";

export const DashboardPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDetail, setShowDetail] = useState(false);
  const rp = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

  const fetchStats = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BACKEND_URL}/api/dashboard/stats`, {
        withCredentials: true
      });
      setData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const summary = data?.summary || {};
  const network = data?.network || {};
  const netRouters = network.routers || [];
  const netOlts = network.olts || [];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Top Banner / Welcome */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-5 rounded-2xl bg-gradient-to-r from-[#0f172a] via-[#1e293b] to-[#0f172a] border border-slate-800 shadow-xl">
        <div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-400 text-[10px] font-bold uppercase tracking-wider">
              ISP Operations Center
            </span>
            <span className="text-xs text-slate-400 font-medium">Bandung Core DC</span>
          </div>
          <h1 className="text-xl sm:text-2xl font-black text-white mt-1">
            Monitoring & Billing Tagihan Internet
          </h1>
          <p className="text-xs text-slate-400 mt-0.5">
            Kelola pelanggan, mikrotik session, OLT fiber optic, dan penerimaan pembayaran secara real-time.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <button
            onClick={fetchStats}
            data-testid="refresh-dashboard-btn"
            className="px-3 py-2 bg-slate-800/80 hover:bg-slate-700 text-slate-200 text-xs font-semibold rounded-xl border border-slate-700 flex items-center gap-1.5 transition-colors"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            <span>Refresh Data</span>
          </button>
          <Link
            to="/keuangan/tagihan"
            data-testid="quick-create-invoice-btn"
            className="px-3.5 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white text-xs font-bold rounded-xl flex items-center gap-1.5 shadow-lg shadow-cyan-600/30 transition-all"
          >
            <Plus className="w-4 h-4" />
            <span>Buat Tagihan</span>
          </Link>
        </div>
      </div>

      {/* Metric Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Pelanggan */}
        <button
          type="button"
          onClick={() => setShowDetail(true)}
          data-testid="total-pelanggan-card"
          className="text-left w-full p-4 rounded-2xl bg-[#0e1628] border border-slate-800/90 shadow-lg relative overflow-hidden group hover:border-cyan-500/60 hover:bg-[#111c33] transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
        >
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-semibold text-slate-400">Total Pelanggan</p>
              <h3 className="text-2xl font-black text-white mt-1">
                {summary.total_pelanggan || 0}
              </h3>
              <div className="flex items-center gap-2 mt-2 text-[11px]">
                <span className="text-emerald-400 font-semibold flex items-center gap-0.5">
                  <CheckCircle className="w-3 h-3" />
                  {summary.pelanggan_aktif || 0} Aktif
                </span>
                <span className="text-slate-600">•</span>
                <span className="text-rose-400 font-semibold flex items-center gap-0.5">
                  <AlertTriangle className="w-3 h-3" />
                  {summary.pelanggan_isolir || 0} Isolir
                </span>
              </div>
            </div>
            <div className="w-10 h-10 rounded-xl bg-cyan-500/15 border border-cyan-500/30 text-cyan-400 flex items-center justify-center">
              <Users className="w-5 h-5" />
            </div>
          </div>
          <div className="mt-3 flex items-center gap-1 text-[10px] font-semibold text-cyan-400/80 group-hover:text-cyan-300">
            <span>Klik untuk rincian & estimasi omzet</span>
            <ChevronRight className="w-3 h-3" />
          </div>
        </button>

        {/* Tagihan Bulan Ini */}
        <div className="p-4 rounded-2xl bg-[#0e1628] border border-slate-800/90 shadow-lg relative overflow-hidden group hover:border-amber-500/40 transition-all">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-semibold text-slate-400">Tagihan Terbit ({data?.periode_bulan || "Periode Aktif"})</p>
              <h3 className="text-xl font-black text-white mt-1">
                Rp {Number(summary.total_tagihan_nominal || 0).toLocaleString("id-ID")}
              </h3>
              <div className="flex items-center gap-1.5 mt-2 text-[11px] text-amber-400 font-semibold">
                <Receipt className="w-3 h-3" />
                <span>{summary.total_unpaid_count || 0} Tagihan Belum Bayar</span>
              </div>
            </div>
            <div className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/30 text-amber-400 flex items-center justify-center">
              <Receipt className="w-5 h-5" />
            </div>
          </div>
        </div>

        {/* Pendapatan Lunas Masuk */}
        <div className="p-4 rounded-2xl bg-[#0e1628] border border-slate-800/90 shadow-lg relative overflow-hidden group hover:border-emerald-500/40 transition-all">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-semibold text-slate-400">Pendapatan Lunas (Cash & VA)</p>
              <h3 className="text-xl font-black text-emerald-400 mt-1">
                Rp {Number((summary.total_lunas_nominal || 0) + (summary.total_pendapatan_lain || 0)).toLocaleString("id-ID")}
              </h3>
              <div className="flex items-center gap-1 mt-2 text-[11px] text-emerald-400 font-semibold">
                <ArrowUpRight className="w-3.5 h-3.5" />
                <span>Collection Rate: {summary.collection_rate || 0}%</span>
              </div>
            </div>
            <div className="w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 flex items-center justify-center">
              <Wallet className="w-5 h-5" />
            </div>
          </div>
        </div>

        {/* Pengeluaran Operasional & Laba */}
        <div className="p-4 rounded-2xl bg-[#0e1628] border border-slate-800/90 shadow-lg relative overflow-hidden group hover:border-purple-500/40 transition-all">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-semibold text-slate-400">Total Pengeluaran Ops ({data?.periode_bulan || "Bulan Ini"})</p>
              <h3 className="text-xl font-black text-rose-400 mt-1">
                Rp {Number(summary.total_pengeluaran || 0).toLocaleString("id-ID")}
              </h3>
              <div className="flex items-center gap-1 mt-2 text-[11px] text-purple-300 font-semibold">
                <span>Laba Bersih: </span>
                <span className={summary.laba_bersih >= 0 ? "text-emerald-400" : "text-rose-400"}>
                  Rp {Number(summary.laba_bersih || 0).toLocaleString("id-ID")}
                </span>
              </div>
            </div>
            <div className="w-10 h-10 rounded-xl bg-purple-500/15 border border-purple-500/30 text-purple-400 flex items-center justify-center">
              <TrendingUp className="w-5 h-5" />
            </div>
          </div>
        </div>
      </div>

      {/* Network Traffic & Router Monitoring Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Bandwidth Traffic per Router (data tersimpan) */}
        <div className="lg:col-span-2 p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-800 pb-3">
            <div>
              <h2 className="text-sm font-bold text-white flex items-center gap-2">
                <Activity className="w-4 h-4 text-cyan-400" />
                <span>Bandwidth Traffic per Router</span>
              </h2>
              <p className="text-xs text-slate-400">
                Agregat trafik tercatat dari {network.total_routers || 0} router MikroTik terdaftar
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5 text-xs bg-cyan-950/40 border border-cyan-800/60 px-2.5 py-1 rounded-lg">
                <span className="w-2 h-2 rounded-full bg-cyan-400"></span>
                <span className="text-slate-300">RX (Down):</span>
                <span className="text-cyan-300 font-mono font-bold">{network.total_traffic_rx || 0} Mbps</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs bg-indigo-950/40 border border-indigo-800/60 px-2.5 py-1 rounded-lg">
                <span className="w-2 h-2 rounded-full bg-indigo-400"></span>
                <span className="text-slate-300">TX (Up):</span>
                <span className="text-indigo-300 font-mono font-bold">{network.total_traffic_tx || 0} Mbps</span>
              </div>
            </div>
          </div>

          <div className="h-64 w-full">
            {netRouters.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={netRouters.map((r) => ({ nama: r.nama, rx: r.rx, tx: r.tx }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="nama" stroke="#64748b" fontSize={10} />
                  <YAxis stroke="#64748b" fontSize={11} unit="M" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#0f172a",
                      borderColor: "#334155",
                      borderRadius: "0.5rem",
                      color: "#f1f5f9",
                      fontSize: "12px"
                    }}
                  />
                  <Bar dataKey="rx" name="Download (RX)" fill="#06b6d4" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="tx" name="Upload (TX)" fill="#6366f1" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-xs text-slate-500" data-testid="dashboard-no-router">
                Belum ada router MikroTik terdaftar. Tambahkan di menu Jaringan → Mikrotik.
              </div>
            )}
          </div>
        </div>

        {/* Quick ISP Status Panel */}
        <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl space-y-4">
          <h2 className="text-sm font-bold text-white border-b border-slate-800 pb-3 flex items-center gap-2">
            <Radio className="w-4 h-4 text-blue-400" />
            <span>Status Perangkat & Core</span>
          </h2>

          <div className="space-y-3 text-xs">
            {netRouters.length === 0 && netOlts.length === 0 && (
              <p className="text-slate-500 italic p-2" data-testid="dashboard-no-device">Belum ada perangkat terdaftar di Master Data Jaringan.</p>
            )}
            {netRouters.map((r, i) => (
              <div key={`router-${i}`} className="p-3 rounded-xl bg-slate-900/80 border border-slate-800 flex justify-between items-center">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-cyan-500/15 text-cyan-400 flex items-center justify-center font-bold">
                    <Router className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-200">{r.nama}</p>
                    <p className="text-[10px] text-slate-400">{r.ip_address} • CPU {r.cpu_load}%</p>
                  </div>
                </div>
                <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${r.status === "online" ? "bg-emerald-500/20 text-emerald-400" : "bg-rose-500/20 text-rose-400"}`}>
                  {(r.status || "offline").toUpperCase()}
                </span>
              </div>
            ))}
            {netOlts.map((o, i) => (
              <div key={`olt-${i}`} className="p-3 rounded-xl bg-slate-900/80 border border-slate-800 flex justify-between items-center">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-purple-500/15 text-purple-400 flex items-center justify-center font-bold">
                    <Radio className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-200">{o.nama}</p>
                    <p className="text-[10px] text-slate-400">{o.active_onus} ONU Terdaftar • Suhu {o.temperature}</p>
                  </div>
                </div>
                <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${o.status === "online" ? "bg-emerald-500/20 text-emerald-400" : "bg-rose-500/20 text-rose-400"}`}>
                  {(o.status || "offline").toUpperCase()}
                </span>
              </div>
            ))}
          </div>

          <div className="pt-2">
            <Link
              to="/jaringan/mikrotik"
              className="block w-full text-center py-2 bg-slate-800 hover:bg-slate-700 text-cyan-400 text-xs font-semibold rounded-xl transition-colors"
            >
              Lihat Detail Monitoring Jaringan →
            </Link>
          </div>
        </div>
      </div>

      {/* Tables: Recent Invoices & Critical Optical Warning */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Invoices */}
        <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl space-y-3">
          <div className="flex items-center justify-between border-b border-slate-800 pb-3">
            <h2 className="text-sm font-bold text-white flex items-center gap-2">
              <Receipt className="w-4 h-4 text-amber-400" />
              <span>Tagihan Terbaru</span>
            </h2>
            <Link
              to="/keuangan/tagihan"
              className="text-xs text-cyan-400 hover:underline font-semibold"
            >
              Semua Tagihan →
            </Link>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-slate-800 text-slate-400 font-semibold">
                  <th className="py-2">No. Invoice</th>
                  <th className="py-2">Pelanggan</th>
                  <th className="py-2">Total</th>
                  <th className="py-2 text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60">
                {(data?.recent_invoices || []).map((inv) => (
                  <tr key={inv.id} className="hover:bg-slate-900/40">
                    <td className="py-2.5 font-mono text-slate-300 font-medium">
                      {inv.no_invoice}
                    </td>
                    <td className="py-2.5">
                      <p className="font-semibold text-white">{inv.pelanggan_nama}</p>
                      <span className="text-[10px] text-slate-400">{inv.paket_nama}</span>
                    </td>
                    <td className="py-2.5 font-bold text-slate-200">
                      Rp {Number(inv.total_tagihan).toLocaleString("id-ID")}
                    </td>
                    <td className="py-2.5 text-center">
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${
                          inv.status === "PAID"
                            ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
                            : "bg-rose-500/20 text-rose-300 border border-rose-500/30"
                        }`}
                      >
                        {inv.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Critical Optical Alerts / Isolir */}
        <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl space-y-3">
          <div className="flex items-center justify-between border-b border-slate-800 pb-3">
            <h2 className="text-sm font-bold text-white flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-rose-400" />
              <span>Peringatan Sinyal Redaman & Isolir</span>
            </h2>
            <Link
              to="/jaringan/olt"
              className="text-xs text-cyan-400 hover:underline font-semibold"
            >
              Monitor OLT →
            </Link>
          </div>

          <div className="space-y-2.5">
            {(data?.critical_onus || []).map((onu) => (
              <div
                key={onu.id}
                className="p-3 rounded-xl bg-slate-900/70 border border-slate-800 flex items-center justify-between"
              >
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-slate-200 text-xs">{onu.nama}</span>
                    <span className="text-[10px] text-slate-400 font-mono">
                      {onu.odp_kode}
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    SN: {onu.onu_sn} • {onu.alamat}
                  </p>
                </div>
                <div className="text-right">
                  <span
                    className={`inline-block font-mono font-bold text-xs px-2 py-0.5 rounded ${
                      onu.status_layanan === "isolir"
                        ? "bg-rose-950/60 text-rose-400 border border-rose-800"
                        : "bg-amber-950/60 text-amber-400 border border-amber-800"
                    }`}
                  >
                    {onu.status_layanan === "isolir" ? "ISOLIR (OFF)" : `${onu.optical_power} dBm`}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {showDetail && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowDetail(false)}>
          <div className="bg-[#0f172a] border border-slate-700 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()} data-testid="pelanggan-detail-modal">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-800 bg-[#090f1d]">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-cyan-400" />
                <h3 className="text-sm font-bold text-white">Rincian Pelanggan & Estimasi Omzet</h3>
              </div>
              <button onClick={() => setShowDetail(false)} className="text-slate-400 hover:text-white p-1 rounded-md" data-testid="close-pelanggan-detail"><X className="w-4 h-4" /></button>
            </div>

            <div className="p-5 space-y-4 overflow-y-auto">
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-xl bg-slate-950/60 border border-slate-800">
                  <p className="text-[11px] text-slate-400">Total</p>
                  <p className="text-lg font-black text-white mt-1">{summary.total_pelanggan || 0}</p>
                </div>
                <div className="p-3 rounded-xl bg-slate-950/60 border border-slate-800">
                  <p className="text-[11px] text-slate-400">Aktif</p>
                  <p className="text-lg font-black text-emerald-400 mt-1">{summary.pelanggan_aktif || 0}</p>
                </div>
                <div className="p-3 rounded-xl bg-slate-950/60 border border-slate-800">
                  <p className="text-[11px] text-slate-400">Isolir</p>
                  <p className="text-lg font-black text-rose-400 mt-1">{summary.pelanggan_isolir || 0}</p>
                </div>
              </div>

              <div className="rounded-xl border border-slate-800 overflow-hidden">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="bg-slate-950/80 text-slate-400 font-semibold">
                      <th className="py-2.5 px-3">Paket</th>
                      <th className="py-2.5 px-3 text-center">Aktif</th>
                      <th className="py-2.5 px-3 text-right">Harga/bln</th>
                      <th className="py-2.5 px-3 text-right">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/60">
                    {(summary.paket_breakdown || []).length === 0 && (
                      <tr><td colSpan={4} className="py-6 text-center text-slate-500" data-testid="detail-empty">Belum ada pelanggan aktif.</td></tr>
                    )}
                    {(summary.paket_breakdown || []).map((p, i) => (
                      <tr key={i} className="hover:bg-slate-900/40" data-testid={`paket-breakdown-row-${i}`}>
                        <td className="py-2.5 px-3 font-semibold text-white">{p.paket_nama}</td>
                        <td className="py-2.5 px-3 text-center text-cyan-300 font-mono">{p.jumlah}</td>
                        <td className="py-2.5 px-3 text-right text-slate-300 font-mono">{rp(p.harga)}</td>
                        <td className="py-2.5 px-3 text-right text-emerald-400 font-mono font-bold">{rp(p.subtotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="p-4 rounded-xl bg-gradient-to-r from-cyan-950/60 to-emerald-950/50 border border-emerald-500/30 flex items-center justify-between">
                <div>
                  <p className="text-[11px] text-slate-300 flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5 text-emerald-400" /> Estimasi Omzet Bulanan</p>
                  <p className="text-[10px] text-slate-500 mt-0.5">Dihitung dari {summary.pelanggan_aktif || 0} pelanggan aktif × harga paket masing-masing</p>
                </div>
                <p className="text-xl font-black text-emerald-400 font-mono" data-testid="omzet-bulanan-estimasi">{rp(summary.omzet_bulanan_estimasi)}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
